// Схема tenants/<slug>/business.json — единственный файл настроек студии.
// Используется конвейером (validate/publish) и тестами. Человеко-ориентированный
// формат: цены в рублях, длительности строкой ("1h30m", "2d"), часы по дням недели.
import { z } from 'zod';

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

const key = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'латиница в нижнем регистре, цифры и дефис');

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'формат ЧЧ:ММ');

/** "90", "90m", "1h30m", "2d", "1d4h" → минуты */
export function parseDuration(input: string | number): number {
  if (typeof input === 'number') return input;
  const s = input.trim().toLowerCase();
  if (/^\d+$/.test(s)) return Number(s);
  const m = /^(?:(\d+)d)?\s*(?:(\d+)h)?\s*(?:(\d+)m)?$/.exec(s);
  if (!m || (!m[1] && !m[2] && !m[3])) return Number.NaN;
  return Number(m[1] ?? 0) * 1440 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
}

const duration = z
  .union([z.string(), z.number().int()])
  .transform((v) => parseDuration(v))
  .pipe(z.number().int().min(5, 'не меньше 5 минут').max(20160, 'не больше 14 суток'));

const bufferDuration = z
  .union([z.string(), z.number().int()])
  .transform((v) => parseDuration(v))
  .pipe(z.number().int().min(0).max(1440, 'буфер не больше суток'));

const interval = z
  .tuple([hhmm, hhmm])
  .refine(([a, b]) => a < b, 'начало должно быть раньше конца');

export const phoneSchema = z
  .string()
  .refine((v) => normalizePhone(v) !== null, 'телефон в формате +7 900 000-00-00');

export function normalizePhone(raw: string): string | null {
  const d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) return `+7${d.slice(1)}`;
  if (d.length === 10) return `+7${d}`;
  if (d.length >= 11 && d.length <= 15) return `+${d}`;
  return null;
}

const imageFile = z
  .string()
  .regex(/^[\w.-]+\.(jpe?g|png|webp)$/i, 'имя файла из папки images (jpg, png, webp)');

export const businessSchema = z
  .object({
    $schema: z.string().optional(),
    slug: z
      .string()
      .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, 'slug: латиница, цифры, дефис, до 40 символов'),
    name: z.string().min(1).max(80),
    shortName: z.string().min(1).max(12, 'короткое имя для иконки — до 12 символов'),
    kind: z.enum(['detailing', 'autoservice', 'tires', 'wash', 'other']).default('other'),
    timezone: z.string().refine(isValidTimezone, 'IANA timezone, например Europe/Moscow'),
    currency: z.string().regex(/^[A-Z]{3}$/).default('RUB'),
    brand: z.object({
      accent: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'цвет акцента #RRGGBB'),
      background: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#0B0C0E'),
    }),
    profile: z.object({
      tagline: z.string().min(1).max(90),
      description: z.string().max(600).default(''),
      address: z.string().min(5).max(160),
      phone: phoneSchema,
      mapUrl: z.url().optional(),
      infoCards: z
        .array(z.object({ title: z.string().min(1).max(40), text: z.string().min(1).max(140) }))
        .max(3)
        .default([]),
    }),
    booking: z
      .object({
        slotStep: duration.default(30),
        minLead: bufferDuration.default(60),
        horizonDays: z.number().int().min(1).max(365).default(45),
        cancelCutoff: z
          .union([z.string(), z.number().int()])
          .transform((v) => parseDuration(v))
          .pipe(z.number().int().min(0).max(10080))
          .default(720),
      })
      .prefault({}),
    resources: z
      .array(z.object({ key, name: z.string().min(1).max(60), kind: z.string().min(1).max(30).default('bay') }))
      .min(1, 'нужен хотя бы один пост'),
    services: z
      .array(
        z.object({
          key,
          name: z.string().min(1).max(80),
          description: z.string().max(600).default(''),
          category: z.string().max(40).default(''),
          price: z.number().min(0).max(1_000_000),
          priceFrom: z.boolean().default(false),
          duration,
          buffer: bufferDuration.default(0),
          sameDay: z.boolean().optional(),
          resources: z.array(key).min(1, 'укажите посты, на которых выполняется услуга'),
          photo: imageFile.optional(),
        }),
      )
      .min(1, 'нужна хотя бы одна услуга'),
    hours: z.object(
      Object.fromEntries(WEEKDAYS.map((d) => [d, z.array(interval).default([])])) as Record<
        Weekday,
        z.ZodDefault<z.ZodArray<typeof interval>>
      >,
    ),
    holidays: z
      .array(z.object({ day: z.iso.date(), note: z.string().max(120).default('') }))
      .default([]),
    images: z.object({
      hero: imageFile,
      logo: imageFile,
      icon: imageFile.optional(),
      works: z
        .array(z.object({ file: imageFile, caption: z.string().max(120).default('') }))
        .max(24)
        .default([]),
    }),
  })
  .superRefine((cfg, ctx) => {
    const resourceKeys = new Set(cfg.resources.map((r) => r.key));
    if (resourceKeys.size !== cfg.resources.length) {
      ctx.addIssue({ code: 'custom', path: ['resources'], message: 'ключи постов повторяются' });
    }
    const serviceKeys = new Set<string>();
    cfg.services.forEach((s, i) => {
      if (serviceKeys.has(s.key)) {
        ctx.addIssue({ code: 'custom', path: ['services', i, 'key'], message: 'ключ услуги повторяется' });
      }
      serviceKeys.add(s.key);
      s.resources.forEach((r, j) => {
        if (!resourceKeys.has(r)) {
          ctx.addIssue({ code: 'custom', path: ['services', i, 'resources', j], message: `нет поста «${r}»` });
        }
      });
      if (s.sameDay === true && s.duration > 1440) {
        ctx.addIssue({
          code: 'custom',
          path: ['services', i, 'sameDay'],
          message: 'многодневная услуга не может быть sameDay',
        });
      }
    });
    const totalIntervals = WEEKDAYS.reduce((n, d) => n + cfg.hours[d].length, 0);
    if (totalIntervals === 0) {
      ctx.addIssue({ code: 'custom', path: ['hours'], message: 'нужен хотя бы один рабочий интервал' });
    }
  });

export type BusinessInput = z.input<typeof businessSchema>;
export type Business = z.output<typeof businessSchema>;

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: tz });
    return tz.includes('/') || tz === 'UTC';
  } catch {
    return false;
  }
}

/** Полезная нагрузка для public.admin_publish_tenant (camelCase, цены в копейках). */
export interface PublishPayload {
  slug: string;
  name: string;
  timezone: string;
  currency: string;
  profile: Record<string, unknown>;
  booking: { slotStepMinutes: number; minLeadMinutes: number; horizonDays: number; cancelCutoffMinutes: number };
  resources: { key: string; name: string; kind: string; sort: number }[];
  services: {
    key: string;
    name: string;
    description: string;
    category: string;
    priceCents: number;
    priceIsFrom: boolean;
    durationMinutes: number;
    bufferMinutes: number;
    sameDay: boolean;
    sort: number;
    resources: string[];
  }[];
  hours: { weekday: number; opens: string; closes: string }[];
  exceptions: { day: string; closed: boolean; note: string }[];
  photos: { key: string; kind: 'hero' | 'logo' | 'work' | 'service'; path: string; caption: string; sort: number; serviceKey?: string }[];
}

/**
 * Превращает проверенный конфиг в payload. resolvePath(file) возвращает путь
 * в Storage, куда конвейер загрузил файл.
 */
export function toPublishPayload(cfg: Business, resolvePath: (file: string) => string): PublishPayload {
  const photos: PublishPayload['photos'] = [
    { key: 'hero', kind: 'hero', path: resolvePath(cfg.images.hero), caption: '', sort: 0 },
    { key: 'logo', kind: 'logo', path: resolvePath(cfg.images.logo), caption: '', sort: 0 },
    ...cfg.images.works.map((w, i) => ({
      key: `work-${i + 1}`,
      kind: 'work' as const,
      path: resolvePath(w.file),
      caption: w.caption,
      sort: i,
    })),
    ...cfg.services
      .filter((s) => s.photo)
      .map((s) => ({
        key: `service-${s.key}`,
        kind: 'service' as const,
        path: resolvePath(s.photo as string),
        caption: s.name,
        sort: 0,
        serviceKey: s.key,
      })),
  ];
  return {
    slug: cfg.slug,
    name: cfg.name,
    timezone: cfg.timezone,
    currency: cfg.currency,
    profile: {
      tagline: cfg.profile.tagline,
      description: cfg.profile.description,
      address: cfg.profile.address,
      phone: normalizePhone(cfg.profile.phone),
      map_url: cfg.profile.mapUrl ?? null,
      info_cards: cfg.profile.infoCards,
      accent: cfg.brand.accent,
      background: cfg.brand.background,
      short_name: cfg.shortName,
      kind: cfg.kind,
    },
    booking: {
      slotStepMinutes: cfg.booking.slotStep,
      minLeadMinutes: cfg.booking.minLead,
      horizonDays: cfg.booking.horizonDays,
      cancelCutoffMinutes: cfg.booking.cancelCutoff,
    },
    resources: cfg.resources.map((r, i) => ({ key: r.key, name: r.name, kind: r.kind, sort: i })),
    services: cfg.services.map((s, i) => ({
      key: s.key,
      name: s.name,
      description: s.description,
      category: s.category,
      priceCents: Math.round(s.price * 100),
      priceIsFrom: s.priceFrom,
      durationMinutes: s.duration,
      bufferMinutes: s.buffer,
      sameDay: s.sameDay ?? s.duration <= 720,
      sort: i,
      resources: s.resources,
    })),
    hours: WEEKDAYS.flatMap((d, i) =>
      cfg.hours[d].map(([opens, closes]) => ({ weekday: i + 1, opens, closes })),
    ),
    exceptions: cfg.holidays.map((h) => ({ day: h.day, closed: true, note: h.note })),
    photos,
  };
}
